import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Newspaper, Calendar, User, MapPin, ArrowLeft, ChevronRight } from 'lucide-react';
import { API_URL, MEDIA_URL } from '../constants';

const colors = ['bg-rose-50 border-rose-200','bg-teal-50 border-teal-200','bg-violet-50 border-violet-200','bg-amber-50 border-amber-200','bg-blue-50 border-blue-200'];

export const NewsListPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/news/?ordering=-date`).then(r => r.json()).then(d => {
      setNews(Array.isArray(d) ? d : (d.results || []));
    }).catch(() => setNews([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Newspaper className="w-8 h-8 text-emerald-700" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Community News</h1>
          <p className="text-gray-600">Latest happenings in Gbongan</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : news.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Newspaper className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No news available yet. Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {news.map((item, i) => (
            <Link key={item.id} to={`/news/${item.id}`} className={`${colors[i % colors.length]} border-2 rounded-2xl p-6 hover:shadow-lg transition block`}>
              {item.image && (
                <div className="mb-4 rounded-xl overflow-hidden h-44">
                  <img src={item.image.startsWith('http') ? item.image : `${MEDIA_URL}/${item.image}`} alt={item.title} className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className="font-bold text-lg text-gray-900 mb-2 leading-tight">{item.title}</h3>
              <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.date ? new Date(item.date).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : ''}</span>
                {(item.author_first_name || item.author_full_name) && <span className="flex items-center gap-1"><User className="w-3 h-3" />{item.author_full_name || `${item.author_first_name} ${item.author_last_name||''}`.trim()}</span>}
              </div>
              {item.content && <p className="text-sm text-gray-600 line-clamp-3">{item.content}</p>}
              <div className="mt-3 flex items-center gap-1 text-emerald-700 text-sm font-medium">Read more <ChevronRight className="w-4 h-4" /></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const NewsDetailPage = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const MEDIA = process.env.REACT_APP_MEDIA_URL || '/media';

  useEffect(() => {
    fetch(`${API_URL}/news/${id}/`).then(r => r.json()).then(d => setNews(d)).catch(() => setNews(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!news) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-gray-500">News article not found.</p>
      <Link to="/news" className="text-emerald-600 hover:underline mt-4 inline-block">← Back to News</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-emerald-600">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/news" className="hover:text-emerald-600">News</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 truncate max-w-xs">{news.title}</span>
      </nav>
      <Link to="/news" className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to News
      </Link>
      <article className="bg-white rounded-2xl shadow-md overflow-hidden">
        {news.image && (
          <div className="h-64 sm:h-80 overflow-hidden">
            <img src={news.image.startsWith('http') ? news.image : `${MEDIA}/${news.image}`} alt={news.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">{news.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 pb-4 border-b mb-6">
            {news.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(news.date).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span>}
            {(news.author_first_name || news.author_full_name) && <span className="flex items-center gap-1.5"><User className="w-4 h-4" />By {news.author_full_name || `${news.author_first_name} ${news.author_last_name||''}`.trim()}</span>}
            {news.ward && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />Ward: {news.ward}</span>}
          </div>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">{news.content || 'No content available for this article.'}</p>
          </div>
          {news.author_contact && (
            <div className="mt-8 bg-emerald-50 border-l-4 border-emerald-600 p-4 rounded">
              <p className="text-sm font-semibold text-emerald-900">Reporter Contact</p>
              <p className="text-sm text-emerald-700 mt-1">{news.author_contact}</p>
            </div>
          )}
        </div>
      </article>
    </div>
  );
};
